import React from "react";
import { notifyWithPriorityHandler as notify } from "src/common/containers/Notifications/util";
import { ACTIVITY_TYPES } from "Constants/constants";
import getUrl from "Libs/getUrl";
import {
  notificationByTitleSelector,
  selectAllowConfigureResourceNotifications,
  updateAllowConfigureResourceNotifications,
  SUCCESS_TIMEOUT
} from "Reducers/notifications";

import type { Activity } from "Api/activity/types";
import type { IntlShape } from "react-intl";
import type { AppDispatch, RootState } from "Store/configureStore";
import { isManualDeploymentEnabledSelector } from "Reducers/environment/deployment";

const isDateTooOld = (time: string) => {
  const limitDate = new Date(time).getTime() + SUCCESS_TIMEOUT;
  const now = Date.now();

  const diffInMilliseconds = now - limitDate;

  return diffInMilliseconds >= SUCCESS_TIMEOUT;
};

type UseActivityNotificationProps = {
  activity: Activity;
  organizationId: string;
  projectId: string;
  dispatch: AppDispatch;
  intl: IntlShape;
  state: RootState;
};

export const configureResourcesNotification = ({
  activity,
  organizationId,
  projectId,
  dispatch,
  intl,
  state
}: UseActivityNotificationProps) => {
  const environmentName = activity.parameters?.environment as string;
  const isManualDeploymentEnabled = isManualDeploymentEnabledSelector(state, {
    projectId,
    environmentId: environmentName,
    organizationId
  });

  const inProgressNotificationTitle = intl.formatMessage({
    id: isManualDeploymentEnabled
      ? "configure_resources.notification.manual_deployement.title"
      : "configure_resources.notification.deploying.title"
  });
  const successNotificationTitle = intl.formatMessage({
    id: "configure_resources.notification.success.title"
  });
  const successNotificationTitleNonGuaranteed = intl.formatMessage({
    id: isManualDeploymentEnabled
      ? "configure_resources.page.notification.manual_deployment.title"
      : "configure_resources.page.notification.title"
  });

  const failureNotificationTitle = intl.formatMessage({
    id: "configure_resources.notification.fail.title"
  });

  const allowNotification = selectAllowConfigureResourceNotifications(state);

  const inProgressNotification = notificationByTitleSelector(state, {
    title: inProgressNotificationTitle
  });

  const successNotification = notificationByTitleSelector(state, {
    title: successNotificationTitle
  });

  const successNotificationNonGuaranteed = notificationByTitleSelector(state, {
    title: successNotificationTitleNonGuaranteed
  });

  const failureNotification = notificationByTitleSelector(state, {
    title: failureNotificationTitle
  });

  if (
    activity.type !== ACTIVITY_TYPES.ENVIRONMENT.RESOURCES.UPDATE ||
    typeof activity?.payload?.has_guaranteed_resource_changes !== "boolean"
  ) {
    return;
  }

  const isGuaranteedResources =
    !!activity?.payload?.has_guaranteed_resource_changes;

  if (allowNotification.inprogress && activity.state === "in_progress") {
    if (
      isDateTooOld(activity.started_at ?? activity.created_at) ||
      inProgressNotification
    ) {
      return;
    }

    const environmentOverviewUrl = getUrl({
      key: "organization.project.environment",
      props: {
        organizationId,
        projectId,
        environmentId: environmentName
      }
    });

    // GUARANTEED RESOURCE (IN PROGRESS)
    if (isGuaranteedResources) {
      dispatch(
        updateAllowConfigureResourceNotifications(["inprogress", false])
      );

      notify(dispatch).loading({
        title: inProgressNotificationTitle,
        description: intl.formatMessage(
          {
            id: isManualDeploymentEnabled
              ? "configure_resources.notification.manual_deployment.description"
              : "configure_resources.notification.deploying.description"
          },
          {
            strong: content => <strong>{content}</strong>,
            environmentName
          }
        ),
        link: {
          title: intl.formatMessage(
            {
              id: isManualDeploymentEnabled
                ? "configure_resources.notification.manual_deployment.linkTitle"
                : "configure_resources.notification.deploying.linkTitle"
            },
            {
              environmentName
            }
          ),
          location: {
            to: environmentOverviewUrl
          },
          closeOnClick: true
        },
        timeout: SUCCESS_TIMEOUT
      });
    } else {
      // NON-GUARANTEED RESOURCE (SUCCESS)
      // If redux state already has the notification, don't show duplicate notifications

      if (successNotificationNonGuaranteed) return;

      dispatch(
        updateAllowConfigureResourceNotifications(["inprogress", false])
      );

      notify(dispatch).success({
        title: successNotificationTitleNonGuaranteed,
        description: intl.formatMessage(
          {
            id: isManualDeploymentEnabled
              ? "configure_resources.page.notification.manual_deployment.description"
              : "configure_resources.page.notification.description"
          },
          {
            strong: content => <strong>{content}</strong>,
            environmentName
          }
        ),
        link: {
          title: intl.formatMessage(
            {
              id: isManualDeploymentEnabled
                ? "configure_resources.page.notification.manual_deployment.linkTitle"
                : "configure_resources.page.notification.linkTitle"
            },
            {
              environmentName
            }
          ),
          location: {
            to: environmentOverviewUrl
          },
          closeOnClick: true
        },
        timeout: SUCCESS_TIMEOUT
      });
    }
  } else if (
    // GUARANTEED RESOURCE (SUCCESS)
    allowNotification.success &&
    activity.state === "complete" &&
    activity.result === "success" &&
    isGuaranteedResources
  ) {
    if (isDateTooOld(activity.completed_at) || successNotification) return;

    dispatch(updateAllowConfigureResourceNotifications(["success", false]));

    notify(dispatch).success({
      title: successNotificationTitle,
      description: intl.formatMessage(
        {
          id: "configure_resources.notification.success.description"
        },
        {
          environmentName: <strong>{environmentName}</strong>
        }
      ),
      link: {
        title: intl.formatMessage({
          id: "configure_resources.notification.success.linkTitle"
        }),
        location: {
          to: getUrl({
            key: "organization.project.environment.log",
            props: {
              organizationId,
              projectId,
              environmentId: environmentName,
              activityId: activity.id
            }
          })
        },
        closeOnClick: true
      },
      timeout: SUCCESS_TIMEOUT
    });
  } else if (
    // (FAILURE)
    allowNotification.failure &&
    activity.state === "complete" &&
    activity.result === "failure" &&
    activity?.expires_at &&
    isGuaranteedResources
  ) {
    if (isDateTooOld(activity.completed_at) || failureNotification) return;

    dispatch(updateAllowConfigureResourceNotifications(["failure", false]));

    const environmentActivityLogUrl = getUrl({
      key: "organization.project.environment.log",
      props: {
        organizationId,
        projectId,
        environmentId: environmentName,
        activityId: activity.id
      }
    });

    notify(dispatch).error({
      title: failureNotificationTitle,
      description: intl.formatMessage(
        {
          id: "configure_resources.notification.fail.description"
        },
        {
          environmentName: <strong>{environmentName}</strong>
        }
      ),
      link: {
        title: intl.formatMessage({
          id: "configure_resources.notification.fail.linkTitle"
        }),
        location: {
          to: environmentActivityLogUrl
        },
        closeOnClick: true
      }
    });
  }
};
